/**
 * @ngdoc service
 * @name contentCreatorAppAPI
 * @description
 * This is the "HTTP client" the frontend uses in order to comunicate with the Express server (and
 * the ContentCreatorApp API).
 *
 * Since most of the requests to the API require authorization, the first method you need to call is
 * `newSession`, that will return a JWT token the service will set on its configuration in order to
 * authorize all the subsequent calls.
 *
 * @memberof common
 */
class ContentCreatorAppAPI {
  /**
   * Class constructor.
   * @param {Service} $http       The Angular `$http` client the service uses to make all the
   *                              requests.
   * @param {Object}  appConstant The app configuration dictionary.
   * @param {Service} appUtils    Because this service uses the utility to format URLs.
   * @ignore
   */
  constructor(
    $http,
    appConstant,
    appUtils,
  ) {
    'ngInject';

    /**
     * The local reference to the `$http` service.
     * @type {Function}
     */
    this.$http = $http;
    /**
     * The local reference to the `appConstant` service.
     * @type {Object}
     */
    this.appConstant = appConstant;
    /**
     * The local reference to the `appUtils` service.
     * @type {Object}
     */
    this.appUtils = appUtils;
    /**
     * The API current version.
     * @type {String}
     */
    this.version = appConstant.version;
    /**
     * The API base URL.
     * @type {String}
     */
    this.url = appConstant.contentCreatorAPI.url;
    /**
     * A dictionary with the API endpoints.
     * @type {Object}
     */
    this.endpoints = appConstant.contentCreatorAPI.endpoints;
    /**
     * The local reference to the `pagination` constant.
     * @type {Object}
     */
    this.pagination = appConstant.pagination;
    /**
     * The token of the currently active session.
     * @type {String|Null}
     */
    this.token = null;
    /**
     * The customerId of the currently active session.
     * @type {?Number}
     */
    this.customerId = null;
  }
  /**
   * Set a JWT token on the service.
   * @param {String} token The new JWT token.
   */
  setToken(token) {
    this.token = token;
  }
  /**
   * Remove any existing token from the service.
   */
  clearToken() {
    this.token = null;
  }
  /**
   * Set the customer Id on the service.
   * @param {Number} customerId The new customerId.
   */
  setCustomerId(customerId) {
    this.customerId = customerId;
  }
  /**
   * Remove any existing customer Id from the service.
   */
  clearCustomerId() {
    this.customerId = null;
  }
  /**
   * Makes a request to get the customer's campaigns.
   * @param {String} page the page of campaigns to get.
   * @return {Promise}
   */
  getCampaigns(page = '1') {
    const params = {
      count: this.pagination.home.pageSize,
      page,
      order: 'desc',
    };

    return this._get(this._getEndpoint('campaigns', params));
  }
  /**
   * Makes a request to get the campaign media.
   * @param {String} campaignId the campaign to get the media from.
   * @param {String} page the page of media to get.
   * @return {Promise}
   */
  getCampaignMedia(campaignId, page = '1') {
    const params = {
      campaignId,
      count: this.pagination.campaign.pageSize,
      page,
      order: 'desc',
    };

    return this._get(this._getEndpoint('campaignMedia', params));
  }
  /**
   * Makes a request for the login of the user.
   * @param {String} username the user name.
   * @param {String} password the user password.
   * @return {Promise}
   */
  login(username, password) {
    return this._post(this._getEndpoint('login'), {
      username,
      password,
    }).then((response) => response.data.data);
  }
  /**
   * Makes a request to get the community media.
   * @param {String} page the page of media to get.
   * @return {Promise}
   */
  getCommunityMedia(page = '1') {
    const params = {
      count: this.pagination.community.pageSize,
      page,
      order: 'desc',
    };

    return this._get(this._getEndpoint('media', params));
  }
  /**
   * Makes a request to upload media.
   * @param {String} url        the url of the media.
   * @param {String} thumbnail  the url of the thumbnail.
   * @param {String} campaignId the campaign to upload the media to.
   * @param {String} caption    the caption of the media.
   * @return {Promise}
   */
  uploadMedia(url, thumbnail, campaignId, caption) {
    const data = {
      content_url: url,
      thumbnail_url: thumbnail,
      campaign_id: campaignId,
      caption,
    };

    return this._post(this._getEndpoint('uploadMedia'), data)
    .then((response) => response.data.data);
  }
  /**
   * Makes a request to get the user media.
   * @param {String} page the page of media to get.
   * @return {Promise}
   */
  getUserMedia(page = '1') {
    const params = {
      count: this.pagination.profile.pageSize,
      page,
      order: 'desc',
    };

    return this._get(this._getEndpoint('userMedia', params));
  }
  /**
   * Mark the TOS as accepted by the current user.
   * @return {Promise}
   */
  setTosAccepted() {
    return this._put(this._getEndpoint('tos'), {
      accept: true,
    });
  }
  /**
   * Get and format an endpoint for the API.
   * @example
   * // Let's say this is the configuration
   * {
   *     version: 'Batman',
   *     contentCreatorAPI: {
   *         url: '/api/{version}',
   *         endpoints: {
   *             myEndpoint: 'my-endpoint/{id}',
   *         },
   *     },
   * }
   *
   *
   * console.log(this._getEndpoint('myEndpoint'));
   * // This will log '/api/Batman/my-endpoint/{id}'
   *
   * console.log(this._getEndpoint('myEndpoint', { id: 12 }));
   * // This will log '/api/Batman/my-endpoint/12'
   *
   * console.log(this._getEndpoint('myEndpoint', { id: 12, limit: 4 }));
   * // This will log '/api/Batman/my-endpoint/12?limit=4'
   *
   * @param {String} name       The name of the endpoint on the app configuration (The
   *                            appConstant constant).
   * @param {Object} parameters Optional. A dictionary with placeholder values for the endpoint.
   *                            If there's no placeholder for one of the paramters, it will be
   *                            added as a query string.
   * @return {String}
   * @throws {Error} If the requested enpdoint is not on the configuration.
   * @ignore
   */
  _getEndpoint(name, parameters = {}) {
    const endpoint = this.endpoints[name];
    if (!endpoint) {
      throw new Error(`Trying to use an unkown endpoint: ${name}`);
    }

    return this.appUtils.formatURL(`${this.url}${endpoint}`, Object.assign(
      {},
      parameters,
      { version: this.version },
    ));
  }
  /**
   * This method is called before every request. If the server has a JWT token,
   * it will automatically add it on the `Authorization` header. If it has the customerId,
   * it will automatically add it on the `x-olapic-customer-id` header.
   * @param {Object} headers The custom headers for a request to be made.
   * @return {Object} The formated headers, with the Authorization header if there was a JWT
   *                  token.
   */
  _formatRequestHeaders(headers) {
    const defaults = {};

    if (this.token) {
      defaults.Authorization = `Bearer ${this.token}`;
    }
    if (this.customerId) {
      defaults[this.appConstant.olapicCustomerHeader] = this.customerId;
    }

    return Object.assign({}, defaults, headers);
  }
  /**
   * Makes a GET request.
   * @param {String} url    The request URL.
   * @param {Object} config Optional. This ends up doing a `$http` call, so this object can have
   *                        any configuration you would want to send to the `$http` service.
   * @return {Promise}
   * @ignore
   */
  _get(url, config = {}) {
    return this._fetch(Object.assign({ url }, config));
  }
  /**
   * Makes a POST request.
   * @param {String} url    The request URL.
   * @param {Object} data   The data you want to send as the request body.
   * @param {Object} config Optional. This ends up doing a `$http` call, so this object can have
   *                        any configuration you would want to send to the `$http` service.
   * @return {Promise}
   * @ignore
   */
  _post(url, data, config = {}) {
    return this._fetch(Object.assign({
      url,
      data,
      method: 'POST',
    }, config));
  }
  /**
   * Makes a PUT request.
   * @param {String} url    The request URL.
   * @param {Object} data   The data you want to send as the request body.
   * @param {Object} config Optional. This ends up doing a `$http` call, so this object can have
   *                        any configuration you would want to send to the `$http` service.
   * @return {Promise}
   * @ignore
   */
  _put(url, data, config = {}) {
    return this._fetch(Object.assign({
      url,
      data,
      method: 'PUT',
    }, config));
  }
  /**
   * Makes a DELETE request.
   * @param {String} url    The request URL.
   * @param {Object} config Optional. This ends up doing a `$http` call, so this object can have
   *                        any configuration you would want to send to the `$http` service.
   * @return {Promise}
   * @ignore
   */
  _delete(url, config = {}) {
    return this._fetch(Object.assign({
      url,
      method: 'DELETE',
    }, config));
  }
  /**
   * Makes a request using `$http`. If the service has a JWT token, this method will call
   * `_formatRequestHeaders` and inject the `Authorization` header.
   * @param {Object} config The configuration for the `$http` call.
   * @return {Promise}
   */
  _fetch(config) {
    config.headers = this._formatRequestHeaders(config.headers || {});
    return this.$http(config);
  }
}

export default ContentCreatorAppAPI;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/contentCreatorAppAPI.js